// scenarioEngine.js
// ─────────────────────────────────────────────────────────────────────────
// MVP heuristic outcome estimator for What-If scenarios, matching the
// "simple heuristics for MVP" note in the PBMP build breakdown (Stage 9,
// Step 5). This is NOT a trained model — it's a deterministic, explainable
// heuristic based on how many players have instructions, how far the
// ball's intended target is from goal, and how much open space the
// instructed run creates. Swap this out for a SoccerNet-trained model later
// without touching the UI — just replace estimateOutcome()'s body.

const GOAL_X = 105; // attacking toward x=105 (PITCH_LENGTH), matches macro_analyzer.py

export function estimateOutcome(scenario, frameRows) {
  const instructions = Object.values(scenario.instructions || {}).filter(Boolean);
  const ballTarget = scenario.ballTarget || null;

  // Base numbers before adjustment
  let passSuccess = 55;
  let shotProb = 8;
  let xT = 0.05;
  let spaceCreated = 0;
  let goalProb = 3;

  // More coached instructions = more coordinated movement = higher success,
  // but with diminishing returns and a complexity penalty past ~4 changes.
  const n = instructions.length;
  passSuccess += Math.min(n, 4) * 6 - Math.max(0, n - 4) * 3;
  spaceCreated = n * 4.2;

  if (ballTarget && frameRows?.length) {
    const distToGoal = Math.hypot(GOAL_X - ballTarget.x, 34 - ballTarget.y);
    const proximityScore = Math.max(0, 1 - distToGoal / GOAL_X); // 0..1
    shotProb += proximityScore * 35;
    xT += proximityScore * 0.4;
    goalProb += proximityScore * 18;

    // Reward targets further from the nearest defender-ish position (any
    // opposing-team row) — crude space heuristic, not real pressure modeling.
    const nearestOppDist = Math.min(
      ...frameRows.filter(r=>r.role!=="ball").map(r=>
        Math.hypot(r.field_x_m - ballTarget.x, r.field_y_m - ballTarget.y)
      ), 999
    );
    if (nearestOppDist < 900) {
      spaceCreated += Math.min(nearestOppDist, 15);
      passSuccess += Math.min(nearestOppDist, 10);
    }
  }

  const clamp = (v, lo=0, hi=100) => Math.max(lo, Math.min(hi, Math.round(v)));

  return {
    passSuccess: clamp(passSuccess),
    shotProb: clamp(shotProb),
    xT: +Math.min(xT, 1).toFixed(2),
    spaceCreated: +spaceCreated.toFixed(1),
    goalProb: clamp(goalProb),
    isHeuristic: true,
  };
}


// Branch creation
export const createBranch = (name, baseFrame, baseState) => ({
  id: Date.now(),
  name,
  baseFrame,
  baseState,
  instructions: [],
  createdAt: new Date().toISOString(),
});

export const addInstruction = (branch, playerId, action, params = {}) => ({
  ...branch,
  instructions: [
    ...branch.instructions.filter(i => i.player_id !== playerId),
    { player_id: playerId, action, params, timestamp: Date.now() }
  ]
});
